import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconArchive, IconArrowBackUp, IconCheck, IconPencil, IconPin, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createKnowledgeMemory,
  listKnowledgeMemories,
  patchKnowledgeMemory,
  type KnowledgeMemory,
  type KnowledgeMemoryKind,
  type KnowledgeMemoryStatus,
} from '@mygpt/app/apis/services/knowledgeLoop';
import { UnitDrawer } from './UnitDrawer';
import { CARD_BORDER, errorDetail, formatJst, proposalColor, unitStatusColor } from './shared';

const KINDS: KnowledgeMemoryKind[] = [
  'reviewer_instruction',
  'rejection_pattern',
  'edit_pattern',
  'style_example',
  'glossary',
  'topic_map',
];
// 6 種すべて人が書ける（絞り込みチップと同じ並び）。書き方の例とトピック地図は主にナレッジ抽出が作るが、手書きの行は上書きされない
const WRITABLE: KnowledgeMemoryKind[] = KINDS;
type View = 'active' | 'proposed' | 'archived';

/** 記憶タブ：このナレッジベースの蒸留が「学んだこと」。蒸留と審査対話のプロンプトにだけ使われ、検索対象にはならない */
export const MemoryPanel = ({ datasetId }: { datasetId: string }) => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('active');
  const [items, setItems] = useState<KnowledgeMemory[]>([]);
  const [counts, setCounts] = useState<Partial<Record<KnowledgeMemoryStatus, number>>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<KnowledgeMemory | 'new' | null>(null);
  const [formKind, setFormKind] = useState<KnowledgeMemoryKind>('reviewer_instruction');
  const [formText, setFormText] = useState('');
  const [openUnit, setOpenUnit] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listKnowledgeMemories(datasetId, { status: view });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      console.error('load memories failed:', e);
      showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
    } finally {
      setLoading(false);
    }
  }, [datasetId, view, t]);

  useEffect(() => {
    load();
  }, [load]);

  // 6 分類は常に見せる（0 件も）。どの分類に何件あるかが一目で分かるように
  const allGroups = useMemo(() => KINDS.map((kind) => ({ kind, rows: items.filter((m) => m.kind === kind) })), [items]);
  const groups = allGroups.filter((g) => g.rows.length);
  const jumpTo = (kind: KnowledgeMemoryKind) =>
    document.getElementById(`memory-group-${kind}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const openEditor = (target: KnowledgeMemory | 'new') => {
    setEditing(target);
    setFormKind(target === 'new' ? 'reviewer_instruction' : target.kind);
    setFormText(target === 'new' ? '' : target.text);
  };

  const submit = async () => {
    if (!editing || formText.trim().length < 2) return;
    setBusy('form');
    try {
      if (editing === 'new') await createKnowledgeMemory(datasetId, { kind: formKind, text: formText.trim() });
      else await patchKnowledgeMemory(datasetId, editing.id, { kind: formKind, text: formText.trim() });
      setEditing(null);
      showNotification({ color: 'green', message: t('knowledge_loop.memory.saved') });
      load();
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (m: KnowledgeMemory, status: 'active' | 'archived') => {
    setBusy(m.id);
    try {
      await patchKnowledgeMemory(datasetId, m.id, { status });
      load();
    } catch (e) {
      showNotification({ color: 'red', message: errorDetail(e) || t('knowledge_loop.save_error') });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as View)}
          data={(['active', 'proposed', 'archived'] as View[]).map((v) => ({
            value: v,
            label: `${t(`knowledge_loop.memory.view_${v}`)}${counts[v] ? ` (${counts[v]})` : ''}`,
          }))}
        />
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => openEditor('new')}>
          {t('knowledge_loop.memory.add')}
        </Button>
      </Group>
      <Text size="xs" c="gray.6">
        {t(`knowledge_loop.memory.view_${view}_desc`)}
      </Text>
      {!loading && items.length > 0 && (
        <Group gap={6}>
          {allGroups.map(({ kind, rows }) => (
            <Tooltip key={kind} label={t(`knowledge_loop.memory.kind_desc.${kind}`)} withArrow multiline w={280}>
              <Button
                size="compact-xs"
                variant={rows.length ? 'light' : 'subtle'}
                color={rows.length ? 'violet' : 'gray'}
                disabled={!rows.length}
                onClick={() => jumpTo(kind)}
                rightSection={
                  <Badge
                    size="xs"
                    variant={rows.length ? 'filled' : 'outline'}
                    color={rows.length ? 'violet' : 'gray'}
                    circle
                  >
                    {rows.length}
                  </Badge>
                }
              >
                {t(`knowledge_loop.memory.kind.${kind}`)}
              </Button>
            </Tooltip>
          ))}
        </Group>
      )}

      {loading ? (
        <Group justify="center" py={60}>
          <Loader size="sm" />
        </Group>
      ) : groups.length === 0 ? (
        <Stack align="center" gap={4} py={60}>
          <Text fw={500}>{t('knowledge_loop.memory.empty')}</Text>
          <Text size="xs" c="gray.6" ta="center" maw={460}>
            {t('knowledge_loop.memory.empty_desc')}
          </Text>
        </Stack>
      ) : (
        groups.map(({ kind, rows }) => (
          <div
            key={kind}
            id={`memory-group-${kind}`}
            style={{ border: CARD_BORDER, borderRadius: 12, background: '#fff', scrollMarginTop: 12 }}
          >
            <Group justify="space-between" px="md" py={10} style={{ borderBottom: CARD_BORDER }}>
              <Group gap={8}>
                <Text fw={600} size="sm">
                  {t(`knowledge_loop.memory.kind.${kind}`)}
                </Text>
                <Badge size="sm" variant="light" color="violet">
                  {rows.length}
                </Badge>
              </Group>
              <Text size="xs" c="gray.6">
                {t(`knowledge_loop.memory.kind_desc.${kind}`)}
              </Text>
            </Group>
            <Stack gap={0}>
              {rows.map((m, i) => (
                <Group
                  key={m.id}
                  wrap="nowrap"
                  align="flex-start"
                  gap="md"
                  px="md"
                  py={10}
                  style={{ borderTop: i ? CARD_BORDER : undefined }}
                >
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.text}
                    </Text>
                    <Group gap={8}>
                      <Badge size="xs" variant="outline" color={m.origin === 'auto' ? 'gray' : 'violet'}>
                        {t(`knowledge_loop.memory.origin.${m.origin}`)}
                      </Badge>
                      {m.pinned && (
                        <Tooltip label={t('knowledge_loop.memory.pinned_desc')} withArrow>
                          <Group gap={2}>
                            <IconPin size={12} color="#7048E8" />
                            <Text size="xs" c="violet">
                              {t('knowledge_loop.memory.pinned')}
                            </Text>
                          </Group>
                        </Tooltip>
                      )}
                      {m.source_unit && (
                        <Tooltip label={t('knowledge_loop.memory.source_unit_hint')} withArrow>
                          <Badge
                            size="xs"
                            variant="light"
                            color={unitStatusColor(m.source_unit.status)}
                            style={{ cursor: 'pointer', textTransform: 'none', maxWidth: 320 }}
                            onClick={() => setOpenUnit(m.source_unit!.id)}
                          >
                            {t('knowledge_loop.memory.source_unit', {
                              title: m.source_unit.title,
                              status: t(`knowledge_loop.unit_status.${m.source_unit.status}`),
                            })}
                          </Badge>
                        </Tooltip>
                      )}
                      <Text size="xs" c="gray.6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t('knowledge_loop.memory.hits', { count: m.hits })}
                        {m.last_used_at ? ` ・ ${formatJst(m.last_used_at)}` : ''}
                        {m.created_by ? ` ・ ${m.created_by}` : ''}
                      </Text>
                    </Group>
                  </Stack>
                  <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                    {view === 'proposed' && (
                      <Tooltip label={t('knowledge_loop.memory.confirm')} withArrow>
                        <ActionIcon
                          variant="light"
                          color="green"
                          loading={busy === m.id}
                          onClick={() => setStatus(m, 'active')}
                        >
                          <IconCheck size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {view === 'archived' ? (
                      <Tooltip label={t('knowledge_loop.memory.restore')} withArrow>
                        <ActionIcon variant="subtle" loading={busy === m.id} onClick={() => setStatus(m, 'active')}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : (
                      <>
                        {WRITABLE.includes(m.kind) && (
                          <Tooltip label={t('knowledge_loop.review.edit')} withArrow>
                            <ActionIcon variant="subtle" color="gray" onClick={() => openEditor(m)}>
                              <IconPencil size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label={t('knowledge_loop.memory.archive')} withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            loading={busy === m.id}
                            onClick={() => setStatus(m, 'archived')}
                          >
                            <IconArchive size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                  </Group>
                </Group>
              ))}
            </Stack>
          </div>
        ))
      )}

      <UnitDrawer datasetId={datasetId} unitId={openUnit} onClose={() => setOpenUnit(null)} onReviewed={load} />

      <Modal
        opened={!!editing}
        onClose={() => setEditing(null)}
        centered
        title={t(editing === 'new' ? 'knowledge_loop.memory.add' : 'knowledge_loop.memory.edit_title')}
      >
        <Stack gap="sm">
          <Select
            label={t('knowledge_loop.memory.field_kind')}
            allowDeselect={false}
            data={WRITABLE.map((k) => ({ value: k, label: t(`knowledge_loop.memory.kind.${k}`) }))}
            value={formKind}
            onChange={(v) => v && setFormKind(v as KnowledgeMemoryKind)}
          />
          <Textarea
            label={t('knowledge_loop.memory.field_text')}
            description={t(`knowledge_loop.memory.kind_desc.${formKind}`)}
            placeholder={t('knowledge_loop.memory.text_placeholder')}
            autosize
            minRows={3}
            maxLength={300}
            value={formText}
            onChange={(e) => setFormText(e.currentTarget.value)}
          />
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.memory.manual_note')}
          </Text>
          <Group justify="flex-end" gap={8}>
            <Button size="xs" variant="default" onClick={() => setEditing(null)}>
              {t('alert_cancel')}
            </Button>
            <Button size="xs" loading={busy === 'form'} disabled={formText.trim().length < 2} onClick={submit}>
              {t('knowledge_loop.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
